import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment.dev';
import { MessageHandlerService } from '../services/message-handler.service';
import { ProgressBarService } from '../services/progress-bar.service';
import { UserDataManager } from '../user/UserDataManager';
import { User } from '../models/user';
import {EMAIL_ALREADY_REGISTERED_COMPANY, ERROR_MESSAGES, RoleID} from '../common/Constants';
import { createEmbeddingContext, DashboardExperience } from 'amazon-quicksight-embedding-sdk';
import { CompanyService } from '../services/company/company.service';
import {Company, insightCompanies} from '../models/company';

@Component({
  selector: 'app-io-insight',
  templateUrl: './io-insight.component.html',
  styleUrls: ['./io-insight.component.scss'],
})
export class IoInsightComponent implements OnInit, OnDestroy {
  currentUser: User;
  dashboardUrl: SafeResourceUrl | null = null;
  isLoading: boolean = false;
  errorMessage: string = '';
  subscriptions: Subscription[] = [];
  genderParam: string = 'Human';
  dashboardId: string | null = null;

  companies: Company[] = [];
  insights: insightCompanies[] = [];
  selectedCompanyId: string | null = null;
  showCompanyDropdown: boolean = false;

  private lastLoadedCompanyId: string | null = null;

  @ViewChild('dashboardContainer', { static: true })
  dashboardContainer!: ElementRef<HTMLDivElement>;

  private dashboardExperience?: DashboardExperience;

  constructor(
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private messageHandlerService: MessageHandlerService,
    private progressBarService: ProgressBarService,
    private userDataManager: UserDataManager,
    private companyService: CompanyService,
  ) {}

  ngOnInit(): void {

    this.subscriptions.push(
      this.userDataManager.user$.subscribe({
        next: (user) => {
          if (user) {
            this.currentUser = user;
            this.showCompanyDropdown =
              user.currentUserRole === RoleID.Admin ||
              user.currentUserRole === RoleID.iohAdmin;

            if (this.showCompanyDropdown) {
              this.loadCompanies();
            } else {
              this.maybeLoadDashboard();
            }
          }
        },
        error: (error) => {
          this.messageHandlerService.generateToastMessage(
            'error',
            ERROR_MESSAGES.USER_PROFILE_FETCH,
            error.message || ERROR_MESSAGES.USER_PROFILE_FETCH,
          );
        },
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.dashboardContainer) {
      this.dashboardContainer.nativeElement.innerHTML = '';
    }
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private loadCompanies(): void {
    const request = { search: '', status: '', pagination: null, ioInsights: true };
    this.subscriptions.push(
      this.companyService.getIOInsightCompaniesList().subscribe({
        next: (res) => {
          if (res?.success && res.content?.data) {
            const data = res.content.data;
            // Assign a new array: p-dropdown only picks up options on reference change, so pushing into the bound array leaves the selected label blank.
            this.insights = Object.keys(data).map((key) => ({
              id: data[key].tenantId,
              name: data[key].companyName,
            }));
          }
        },
        error: (error) => {
          this.messageHandlerService.generateToastMessage(
            'error',
            'Failed to load companies',
            error?.message || 'Could not fetch companies',
          );
        },
      }),
    );
  }

  onCompanyChange(): void {
    this.lastLoadedCompanyId = null;
    this.maybeLoadDashboard();
  }

  /** Company key used for deduping loads: selected tenant for super/IOH admin, or profile companyId for company admin. */
  private resolveDashboardCompanyKey(): string | null {
    if (this.showCompanyDropdown) {
      return this.selectedCompanyId ?? null;
    }
    if (
      this.currentUser?.currentUserRole === RoleID.companyAdmin &&
      this.currentUser?.companyId
    ) {
      return this.currentUser.companyId;
    }
    return null;
  }

  private maybeLoadDashboard(): void {
    if (!this.currentUser) return;

    if (this.showCompanyDropdown && !this.selectedCompanyId) return;

    const effectiveKey = this.resolveDashboardCompanyKey();
    if (this.lastLoadedCompanyId === effectiveKey) {
      return;
    }

    this.lastLoadedCompanyId = effectiveKey;
    this.loadQuickSightDashboard();
  }

  private loadQuickSightDashboard(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.progressBarService.setProgressBarVisibility(true);

    if (this.dashboardContainer) {
      this.dashboardContainer.nativeElement.innerHTML = '';
    }

    const payload: any = { };
    if (this.selectedCompanyId) {
      payload.companyId = this.selectedCompanyId;
    } else if (
      this.currentUser?.currentUserRole === RoleID.companyAdmin &&
      this.currentUser?.companyId
    ) {
      payload.companyId = this.currentUser.companyId;
    }

    this.subscriptions.push(
      this.http
        .post<{ embedUrl: string; dashboardId: string; expiresAt: string }>(
          environment.endPoints.companyDashboard,
          payload,
        )
        .subscribe({
          next: async (response) => {
            console.log('response', response);
            await this.embedDashboard(response.embedUrl);
            this.isLoading = false;
            this.progressBarService.setProgressBarVisibility(false);
          },
          error: (error) => {
            this.errorMessage =
              error?.errorMessage ||
              error?.message ||
              'Failed to load QuickSight dashboard';
            this.messageHandlerService.generateToastMessage(
              'error',
              'Dashboard Load Failed',
              this.errorMessage,
            );
            this.isLoading = false;
            this.progressBarService.setProgressBarVisibility(false);
          },
        }),
    );
  }

  private async embedDashboard(embedUrl: string): Promise<void> {
    try {
      if (!this.dashboardContainer || !this.dashboardContainer.nativeElement) {
        throw new Error('Dashboard container element is not available');
      }

      const embeddingContext = await createEmbeddingContext();

      const dashboardParameters = [
        {
          Name: 'GenderParam2',
          Values: [this.genderParam || 'Human'],
        },
      ];

      this.dashboardExperience = await embeddingContext.embedDashboard(
        {
          url: embedUrl,
          container: this.dashboardContainer.nativeElement,
          width: '100%',
          height: '800px',
        },
        {
          parameters: dashboardParameters,
          onMessage: (message) => {
            console.log('Dashboard message:', message);
          },
        },
      );
    } catch (error) {
      console.error('Failed to embed dashboard:', error);
      this.errorMessage = 'Failed to embed QuickSight dashboard';
      this.messageHandlerService.generateToastMessage(
        'error',
        'Dashboard Embed Failed',
        this.errorMessage,
      );
    }
  }

  async updateDashboardParameters(parameters: Array<{ Name: string; Values: string[] }>): Promise<void> {
    if (this.dashboardExperience) {
      try {
        await this.dashboardExperience.setParameters(parameters);
        console.log('Dashboard parameters updated successfully');
      } catch (error) {
        console.error('Failed to update dashboard parameters:', error);
        this.messageHandlerService.generateToastMessage(
          'error',
          'Parameter Update Failed',
          'Failed to update dashboard parameters',
        );
      }
    }
  }

  onRetry(): void {
    this.lastLoadedCompanyId = null;
    this.maybeLoadDashboard();
  }

  onSubmitGenderParam(): void {
    const trimmedParam = this.genderParam?.trim();

    if (!trimmedParam) {
      this.messageHandlerService.generateToastMessage(
        'warn',
        'Invalid Input',
        'Please enter a valid Gender Param value',
      );
      return;
    }

    this.genderParam = trimmedParam;

    if (this.dashboardExperience) {
      const dashboardParameters = [
        {
          Name: 'GenderParam2',
          Values: [this.genderParam],
        },
      ];
      this.updateDashboardParameters(dashboardParameters);
    } else if (this.currentUser) {
      this.lastLoadedCompanyId = null;
      this.maybeLoadDashboard();
    }
  }
}
